import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  inject,
  Injector,
  Input,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { CRMIntegrationsService } from '../../../services/crm-integrations.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { publicMethods } from '../../../globals';
import { forkJoin, of } from 'rxjs';
import { catchError, concatMap, filter, tap } from 'rxjs/operators';
import { ZintAutocompleteComponent } from '../../../app/html-snippet-components/zint-autocomplete/zint-autocomplete.component';
import { MessageService } from '../../../services/message.service';
import { ZintModalConfig, ZintModalContent } from '../../../classes/types';
import { someCompanyIdsForTestExport } from '../../../classes/company';
declare var $: any;

export interface Mapping {
  zintProperty: Record<string, any>;
  crmField: Record<string, any>;
  overwrite: boolean;
  id?: number | string;
}

@Component({
  selector: 'wizard-crm-export-mapping',
  templateUrl: './wizard-crm-export-mapping.component.html',
  styleUrl: './wizard-crm-export-mapping.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class WizardCrmExportMappingComponent {
  @ViewChildren(ZintAutocompleteComponent)
  autocompletes!: QueryList<ZintAutocompleteComponent>;

  @Input() stepData: Record<string, any>;

  crmService = inject(CRMIntegrationsService);
  snackbar = inject(MatSnackBar);
  pubMethods = inject(publicMethods);
  messageService = inject(MessageService);
  cdr = inject(ChangeDetectorRef);
  injector = inject(Injector);

  CRM_OBJECT_TYPE = 'crm_object_type';
  LIST_CRM_FIELDS_OPTIONS = 'list_crm_object_fields';
  LIST_ZINT_FIELDS_OPTIONS = 'list_zint_options';
  EXISTING_EXPORT_MAPPINGS = 'export_mappings';
  CRM_OBJECT_FIELDS = 'crm_object_fields';

  crmType: string = '';
  stepType: string = '';
  crmObjectID = computed(() => {
    return this.crmService.configCRMSelectedObjectId();
  });

  currentZintFieldType: string = '';

  customZintConstant: string = '';
  showCustomConstantModal = false;
  customZintProperty: Record<string, any> = null;

  crmProperties = [];

  zintProperties = [];

  selectedFieldMappings: Mapping[] = [];

  defaultMapping: Mapping = {
    zintProperty: {},
    crmField: {},
    overwrite: true,
  };

  shouldShowTestExportModal = false;
  randomIds: string[] = [];

  showOverwriteHelpModal = false;
  overwriteHelpModalConfig: ZintModalConfig;
  overwriteHelpModalContent: ZintModalContent = {};

  ngOnInit() {
    this.crmType = this.crmService.configCRMType();
    this.stepType = this.stepData.stepType;
    this.getStepActionContent();

    this.selectedFieldMappings.push({ ...this.defaultMapping });
  }

  getStepActionContent(): void {
    if (!this.crmObjectID()) {
      this.crmService
        .getCRMExistingState({ actionType: this.CRM_OBJECT_TYPE })
        .subscribe(data => {
          if (this.pubMethods.hasData(data)) {
            this.crmService.configCRMSelectedObjectId.update(() => data[0]?.id);

            this.listZintAndCRMFieldsAndGetExistingMappings();
          }
        });
    } else if (this.crmObjectID()) {
      this.listZintAndCRMFieldsAndGetExistingMappings();
    }
  }

  listZintAndCRMFieldsAndGetExistingMappings(): void {
    forkJoin({
      zintFields: this.crmService.getCRMStepActionContent({
        stepType: this.stepType,
        actionType: this.LIST_ZINT_FIELDS_OPTIONS,
        objectId: this.crmObjectID(),
      }),

      existingMappings: this.crmService.getCRMExistingState({
        actionType: this.EXISTING_EXPORT_MAPPINGS,
        objectId: this.crmObjectID(),
      }),
    })
      .pipe(
        tap(results => {
          this.zintProperties = results.zintFields;

          this.handleExistingMappings(results.existingMappings);
        }),
        catchError(error => {
          console.error('export mapping err', error);
          return of([]);
        })
      )
      .subscribe();
  }

  handleExistingMappings(existingData: Record<string, any>[]): void {
    if (!existingData?.length) return;
    this.shouldShowTestExportModal = true;
    // reverse() to show latest entry first
    existingData.reverse().forEach(mapping => {
      this.selectedFieldMappings = [
        ...this.selectedFieldMappings,
        {
          crmField: { label: mapping.crmFieldLabel },
          zintProperty: { displayName: mapping.displayName },
          overwrite: mapping.overwrite,
          id: mapping.crmField,
        },
      ];
    });
  }

  getCRMFieldsFromZintType(selectedZintProperty, selectedIndex?: any): void {
    if (
      selectedZintProperty.constant &&
      selectedZintProperty.constant === 'ANY CONSTANT'
    ) {
      this.showCustomConstantModal = true;
      this.cdr.detectChanges();
      $('#customZintConstantModal').modal();

      $('#customZintConstantModal').on('hide.bs.modal', e => {
        this.showCustomConstantModal = false;
      });

      return;
    }

    this.updateSelectedPropertyAtIndex(
      selectedIndex,
      'zintProperty',
      selectedZintProperty
    );
    const { zintDataType } = selectedZintProperty;

    if (
      this.currentZintFieldType &&
      this.currentZintFieldType === zintDataType
    ) {
      return;
    }

    this.currentZintFieldType = zintDataType;
    this.fetchCRMFields();
  }

  fetchCRMFields(): void {
    /* extracted so it can be called as part of getCRMFieldsFromZintType
     * and manually refreshing fields.
     */
    const queryParamToAppend = `?zint_field_type=${this.currentZintFieldType || 'Text'}`;

    this.crmService
      .getCRMStepActionContent({
        stepType: this.stepType,
        actionType: this.LIST_CRM_FIELDS_OPTIONS,
        objectId: this.crmObjectID(),
        qParam: queryParamToAppend,
        refetch: true,
      })
      .subscribe(data => {
        this.crmProperties = data;
      });
  }

  confirmCustomConstant() {
    const customConstantObj = {
      id: null,
      crmField: null,
      crmFieldLabel: null,
      overwrite: false,
      displayName: this.customZintConstant,
      constant: this.customZintConstant,
      zintDataType: 'Text',
      mappingType: 'StringConstantCRMExportFieldMapping',
    };

    this.zintProperties = [...this.zintProperties, customConstantObj];
    this.customZintProperty = customConstantObj;
    this.getCRMFieldsFromZintType(customConstantObj, 0);
    $('#customZintConstantModal').modal('hide');
    this.showCustomConstantModal = false;
    this.customZintConstant = ''; //reset input in modal
  }

  removeCustomConstantObjectAfterMapping() {
    this.zintProperties = this.zintProperties.filter(
      obj => !obj.constant || obj.constant === 'ANY CONSTANT'
    );
  }

  updateSelectedPropertyAtIndex(
    index: number,
    property: keyof Mapping,
    data: Record<string, any>
  ) {
    this.selectedFieldMappings[index][property as string] = data;
  }

  confirmMappingAndAddMore(mapIndex: number): void {
    const foundMapping = this.selectedFieldMappings[mapIndex];

    const areFieldsValid =
      this.pubMethods.hasData(foundMapping.crmField) &&
      this.pubMethods.hasData(foundMapping.zintProperty);

    if (!areFieldsValid) {
      this.snackbar.open('Select both mappings', 'X', {
        duration: 2200,
      });
      return;
    }

    const reqBodyForCrmField = { ...foundMapping.crmField, lookupField: null };
    let fieldIdToPostZintProperty = null;

    // POST crmField to get fieldID back first
    this.crmService
      .postCRMStepActionJSON({
        postActionType: this.CRM_OBJECT_FIELDS,
        objectId: this.crmObjectID(),
        params: reqBodyForCrmField,
      })
      .pipe(
        tap(response => {
          // get the fieldID for the following POST call
          if (response.ok) {
            fieldIdToPostZintProperty = response.body.id;
            this.selectedFieldMappings[mapIndex].id = response.body.id;
          } else {
            const errorDetails = response.error?.error || response.body;
            if (errorDetails.nonFieldErrors) {
              const errMsg = errorDetails.nonFieldErrors;
              this.messageService.show(errMsg);
            } else {
              console.warn('something went wrong', response);
            }
          }
        }),
        filter(response => response.ok), //if error stop 2nd call
        concatMap(() => {
          const {
            overwrite,
            zintProperty: {
              zintDataType,
              zintAttribute,
              mappingType,
              customColumn,
              propensityModel,
              constant,
              researchQuestion,
              key,
              responseJsonPath
            },
          } = foundMapping;

          const reqBodyForZintField = {
            crmField: fieldIdToPostZintProperty,
            customColumn,
            propensityModel,
            constant,
            overwrite,
            zintAttribute,
            zintDataType,
            mappingType,
            researchQuestion,
            key,
            responseJsonPath
          };

          return this.crmService.postCRMStepActionJSON({
            postActionType: this.EXISTING_EXPORT_MAPPINGS,
            objectId: this.crmObjectID(),
            params: reqBodyForZintField,
          });
        }),
        tap(response => {
          if (response.ok) {
            this.shouldShowTestExportModal = true;
            this.addMappingsSelectRow();
            this.resetAllMappingSelects();
            this.crmService.getCRMConfigStepStatus(this.stepData.stepType);
          } else {
            console.warn('error in 2nd post::', response);
          }
        })
      )
      .subscribe();
  }

  addMappingsSelectRow(): void {
    // This adds a new dropdown row at the top.
    this.selectedFieldMappings.unshift({
      ...this.defaultMapping,
    });
  }

  resetAllMappingSelects(): void {
    this.removeCustomConstantObjectAfterMapping();
    this.autocompletes.forEach(component => component.reset());
  }

  removeMapping(mapIndex: number) {
    /**
     * DELETE /crm_framework/crms/hubspot/object/{crmObjectPk}/field/{id}/
     */
    const mappingToDelete = this.selectedFieldMappings[mapIndex];

    if (!mappingToDelete) return;

    const { id } = mappingToDelete;

    this.crmService
      .updateCRMStepActionContent({
        updateActionType: this.CRM_OBJECT_FIELDS,
        objectId: this.crmObjectID(),
        fieldId: Number(id),
        updateMethod: 'delete',
      })
      .subscribe(response => {
        if (response.ok) {
          this.selectedFieldMappings = this.selectedFieldMappings.filter(
            (_, index) => index !== mapIndex
          );
        }
      });
  }

  formatCRMFieldLabel(): string {
    if (!this.pubMethods.hasData(this.selectedFieldMappings[0]?.zintProperty))
      return 'CRM Property';

    const typeHint = this.currentZintFieldType
      ? `(${this.currentZintFieldType})`
      : '';

    return `CRM Property ${typeHint}`;
  }

  openTestExport(): void {
    this.randomIds = this.pubMethods.getRandomElementsFromArray(
      someCompanyIdsForTestExport,
      5
    );
    this.cdr.detectChanges();
    $('#exportToCRMModal').modal();
  }

  toggleOverwriteHelp(): void {
    const explainer = `Checked (Overwrite): Zint will replace any existing data in the CRM field.
- E.g. CRM field is "Software." Zint sends "Technology." The CRM field becomes "Technology."\n
Unchecked (Do Not Overwrite): Zint will only add data if the CRM field is empty.
- E.g. CRM field is "Software." Zint sends "Technology." The CRM field remains "Software."
    `;

    this.overwriteHelpModalContent = {
      plainText: explainer,
    };
    this.overwriteHelpModalConfig = {
      name: 'overwriteHelp',
      size: 'lg',
      title: `What does "Overwrite" mean?`,
      showHeaderRow: true,
      vCentered: true,
    };

    this.showOverwriteHelpModal = true;
    this.cdr.detectChanges();
    this.pubMethods.triggerZintModal(this.overwriteHelpModalConfig.name);
  }
}
